import React, { useContext, useEffect, useState } from 'react';
import { CancelTokenSource } from 'axios';
import { NextPageContext } from 'next';
import { addDays } from 'date-fns';
import {
  CmsDestination,
  CmsExperiencePhase,
  CmsMemory
} from '../integrations/contentful/types';
import {
  fetchDestination,
  fetchMemoriesByLinkedEntry
} from '../integrations/contentful/queries';
import H from '../components/elements/H';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import Container from '../components/grid/Container';
import { navbarLayout } from '../layouts/navbarLayout';
import BackButton from '../components/navigation/BackButton';
import { baseXUnit, breakpoints } from '../styles/theme';
import ExpandableContent from '../components/common/ExpandableContent';
import CmsTextareaContent from '../components/cms/CmsTextareaContent';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import {
  IMTFactsResponse,
  IMTTripPhaseCities,
  IMTTripReservingDetails
} from '../integrations/erp/apiTypes';
import {
  formatCurrency,
  formatPopulation,
  formatTimeDifference
} from '../components/destinations/factBoxFormatters';
import HorizontalLine from '../components/common/HorizontalLine';
import HorizontalCardList, {
  VisibleCards
} from '../components/common/HorizontalCardList';
import { renderMemoryCardSafely } from '../components/memories/MemoryCard';
import ExperiencePhaseModalContent from '../components/destinations/ExperiencePhaseModalContent';
import BasicModal from '../components/common/BasicModal';
import {
  fetchCityFacts,
  fetchTripPhaseCities,
  fetchTripReservingDetails
} from '../integrations/erp/queries';
import Breadcrumbs from '../components/head/Breadcrumbs';
import getUseEffectRequestWithCleanup from '../utils/getUseEffectRequestWithCleanup';
import PriceBox from '../components/common/sticky-pricebox/PriceBox';
import StickyBottomBox from '../components/common/sticky-pricebox/StickyBottomBox';
import TravelInfoBox from '../components/common/sticky-pricebox/TravelInfoBox';
import { LoadingState } from '../loading/loadable';
import { filterMemories } from '../search/searchUtils';
import { extractExperiencePhasesFromMemories } from '../utils/memories';
import PhaseListSection from '../components/destinations/PhaseListSection';
import { arrayHead } from '../utils/array';
import DisplayOn from '../components/common/DisplayOn';
import CmsMediaFixedHeight from '../components/cms/CmsMediaFixedHeight';
import Icon from '../components/common/Icon';
import FlexDiv from '../components/common/FlexDiv';
import TripContentCard from '../components/destinations/TripContentCard';
import {
  DesktopHorizontalList,
  getDesktopHorizontalListTopControls
} from '../components/common/DesktopHorizontalList';
import DestinationFactBoxList from '../components/destinations/DestinationFactBoxList';
import DivButton from '../components/a11y/DivButton';
import MediaGalleryModal from '../components/media-gallery/MediaGalleryModal';
import { trackAnalyticsEvent } from '../analytics/analytics';
import { configuration } from '../utils/environment';
import { destinationListPageBreadcrumbItem } from './DestinationListPage';
import { TimPage } from './TimPage';
import { SearchContext } from './_app';

async function fetchTripReservingDetailsForDestination(
  imtTripId: number,
  source: CancelTokenSource
): Promise<IMTTripReservingDetails | undefined> {
  const searchStartDate = addDays(new Date(), 1);
  const searchEndDate = addDays(searchStartDate, 7);
  return fetchTripReservingDetails(
    imtTripId,
    { searchStartDate, searchEndDate, prioritizePreferredDuration: true },
    source
  );
}

interface DestinationPageProps {
  destination?: CmsDestination;
  imtTripId?: number;
  imtTripPhaseCities: IMTTripPhaseCities[];
  facts?: IMTFactsResponse;
  relatedMemories: CmsMemory[];
}

interface MediaGalleryState {
  show: boolean;
}

const priceBoxHeight = 9;
const ANALYTICS_CATEGORY = 'Matkakohdesivu';

const DestinationPage: TimPage<DestinationPageProps> = ({
  destination,
  imtTripId,
  imtTripPhaseCities,
  facts,
  relatedMemories
}) => {
  const [mediaGalleryState, setMediaGalleryState] =
    React.useState<MediaGalleryState>({
      show: false
    });
  const searchContext = useContext(SearchContext);
  const [tripReservationData, setTripReservationData] = useState<
    IMTTripReservingDetails | undefined
  >(undefined);
  const [tripReservationDataLoadingState, setTripReservationDataLoadingState] =
    useState<LoadingState>(LoadingState.STARTED);
  useEffect(() => {
    return getUseEffectRequestWithCleanup(async (source: CancelTokenSource) => {
      if (imtTripId) {
        const reservationData = await fetchTripReservingDetailsForDestination(
          imtTripId,
          source
        );
        setTripReservationDataLoadingState(
          reservationData ? LoadingState.DONE : LoadingState.FAILED
        );
        setTripReservationData(reservationData);
      } else {
        setTripReservationDataLoadingState(LoadingState.FAILED);
      }
    });
  }, [imtTripId]);

  const [experiencePhaseToShow, setExperiencePhaseToShow] = useState<
    CmsExperiencePhase | undefined
  >(undefined);

  const openExperienceDialog = (experiencePhase: CmsExperiencePhase) => {
    setExperiencePhaseToShow(experiencePhase);
    trackAnalyticsEvent(
      ANALYTICS_CATEGORY,
      'Avattu lomakokemusmodaali',
      experiencePhase.fields.title
    );
  };
  const closeExperienceDialog = () => setExperiencePhaseToShow(undefined);

  const [tripDetailsBoxVisibility, setTripDetailsBoxVisibility] =
    useState<boolean>(false);

  function onPriceBoxInfoButtonClick() {
    setTripDetailsBoxVisibility(!tripDetailsBoxVisibility);
    trackAnalyticsEvent(
      ANALYTICS_CATEGORY,
      'Avattu matkan lisätiedot varauspainikkeesta',
      destination ? destination.fields.title : ''
    );
  }

  if (!destination) {
    return null;
  }
  const imageHeightBase = [41, 41, 77];
  const imageWidthBase = 77;

  const memoriesByPhaseIds: Record<string, CmsMemory> = {};
  // Group memories by phase ids.
  relatedMemories.forEach((memory) => {
    memory.fields.phases.forEach((phase) => {
      memoriesByPhaseIds[phase.sys.id] = memory;
    });
  });

  const currency =
    (facts &&
      facts.countryFacts &&
      formatCurrency(facts.countryFacts.currencyCode)) ||
    undefined;
  const population =
    (facts &&
      facts.cityFacts.population &&
      formatPopulation(facts.cityFacts.population)) ||
    undefined;
  const timeDifference =
    (facts &&
      facts.cityFacts.timeZone &&
      formatTimeDifference(facts.cityFacts.timeZone)) ||
    undefined;

  const experiencePhases = extractExperiencePhasesFromMemories(relatedMemories);
  const answers = searchContext.answers.getAll();
  const recommendedExperiencePhases =
    relatedMemories && Object.keys(answers).length > 0
      ? extractExperiencePhasesFromMemories(
          filterMemories(relatedMemories, answers)
        )
      : [];
  const canonicalPath = generatePath(Paths.Destination, {
    slug: destination.fields.slug
  });

  const shipTrip = imtTripId
    ? configuration.erpShipTripIds.includes(imtTripId)
    : false;

  let experiencePhaseModalContent;
  if (experiencePhaseToShow) {
    const memory = memoriesByPhaseIds[experiencePhaseToShow.sys.id];
    const traveller = arrayHead(memory.fields.travellers);
    experiencePhaseModalContent =
      experiencePhaseToShow.fields.mainMedia &&
      traveller &&
      traveller.fields.profilePicture ? (
        <ExperiencePhaseModalContent
          experiencePhase={{
            ...experiencePhaseToShow.fields,
            mainMedia: experiencePhaseToShow.fields.mainMedia
          }}
          memory={{
            id: memory.sys.id,
            name: memory.fields.title,
            slug: memory.fields.slug,
            startDate: memory.fields.startDate,
            endDate: memory.fields.endDate
          }}
          traveller={{
            id: traveller.sys.id,
            name: traveller.fields.title,
            picture: traveller.fields.profilePicture,
            slug: traveller.fields.slug
          }}
        />
      ) : null;
  }

  return (
    <div data-cy="destination-page">
      <SEO
        title={
          (destination.fields.seoTitle || destination.fields.title) +
          ' - Matkakohde'
        }
        canonicalPath={canonicalPath}
        description={
          destination.fields.seoDescription || destination.fields.leadParagraph
        }
        mainImage={destination.fields.mainImage}
      />
      <Breadcrumbs
        items={[
          destinationListPageBreadcrumbItem,
          {
            name: destination.fields.seoTitle || destination.fields.title,
            path: canonicalPath
          }
        ]}
      />
      <Container>
        <Row>
          <Col size={24}>
            <Spacing bottom={[4, 4, 7]} top={[5, 5, 8]}>
              <Spacing bottom={1}>
                <H styling="h5">{t('Matkakohde')}</H>
              </Spacing>
              <H styling="h1">
                <strong>
                  {destination.fields.title}, {destination.fields.country}
                </strong>
              </H>
            </Spacing>
          </Col>
        </Row>
        <Row>
          <Col size={[24, 24, 11]}>
            <Spacing bottom={6}>
              {destination.fields.mainImage && (
                <DivButton
                  data-cy="destination-page-main-media"
                  onClick={() => {
                    setMediaGalleryState({ show: true });
                    trackAnalyticsEvent(
                      ANALYTICS_CATEGORY,
                      'Avattu mediagalleriamodaali',
                      destination.fields.mainImage &&
                        destination.fields.mainImage.fields.title
                    );
                  }}
                >
                  <CmsMediaFixedHeight
                    media={destination.fields.mainImage}
                    fixedHeight={imageHeightBase}
                    maxWidth={imageWidthBase}
                    borderRadius={1}
                    hover
                  />
                </DivButton>
              )}
            </Spacing>
          </Col>
          <Col size={[0, 0, 1]}></Col>
          <Col size={[24, 24, 12]}>
            <div className="ingress">
              <H styling="h3" tag="div">
                {destination.fields.leadParagraph}
              </H>
              <Spacing top={[3, 3, 4]} bottom={9}>
                <div className="content">
                  <DisplayOn small medium>
                    <ExpandableContent
                      onClick={() => {
                        trackAnalyticsEvent(
                          ANALYTICS_CATEGORY,
                          'Avattu matkakohteen koko teksti',
                          destination.fields.title
                        );
                      }}
                    >
                      <CmsTextareaContent
                        content={destination.fields.description}
                      />
                    </ExpandableContent>
                  </DisplayOn>
                  <DisplayOn large>
                    <CmsTextareaContent
                      content={destination.fields.description}
                    />
                  </DisplayOn>
                </div>
              </Spacing>
            </div>
            {facts && (
              <DestinationFactBoxList
                population={population}
                currency={currency}
                timeDifference={timeDifference}
              />
            )}
          </Col>
        </Row>
        <Row>
          <Col size={[24, 24, 7]}>
            <Spacing top={11}>
              <FlexDiv>
                <H styling="h2">
                  <strong>{t('Matkan sisältö')}</strong>
                </H>
                <DisplayOn large>
                  <Spacing left={1}>
                    <Icon name="nav-arrow-right" size={4} color="light-gray" />
                  </Spacing>
                </DisplayOn>
              </FlexDiv>
            </Spacing>
          </Col>
          <Col size={[0, 0, 1]} />
          <Col size={[24, 24, 16]}>
            <Spacing top={7} bottom={8}>
              <TripContentCard
                imtTripPhaseCities={imtTripPhaseCities}
                tripReservationData={tripReservationData}
                tripReservationDataLoadingState={
                  tripReservationDataLoadingState
                }
                shipTrip={shipTrip}
              />
            </Spacing>
          </Col>
        </Row>
      </Container>
      {experiencePhases.length > 0 && <HorizontalLine expand={false} />}
      <Container>
        {experiencePhases.length > 0 && (
          <Row>
            <Col size={[24, 24, 7]}>
              <Spacing top={[8, 8, 20]} bottom={8}>
                <H styling={'h2'}>
                  <strong>{t('Lomakokemuksia')}</strong>
                </H>
                <H styling="h5" tag="div" color="gray" data>
                  {t('Kohteessa')} {destination.fields.title}
                </H>
              </Spacing>
            </Col>
            <Col size={[0, 0, 1]} />
            <Col size={[24, 24, 16]}>
              <Spacing top={[0, 0, 13]}>
                <div data-testid="travel-experiences">
                  {recommendedExperiencePhases.length > 0 &&
                    recommendedExperiencePhases.length <
                      experiencePhases.length && (
                      <div data-testid="recommended-phases">
                        <PhaseListSection
                          title={t('Tim suosittelee')}
                          iconName="travel-favourites"
                          openDialog={openExperienceDialog}
                          phases={
                            recommendedExperiencePhases as CmsExperiencePhase[]
                          }
                        />
                      </div>
                    )}
                  <PhaseListSection
                    title={t('Tekemistä')}
                    iconName="travel-activity"
                    openDialog={openExperienceDialog}
                    phases={experiencePhases as CmsExperiencePhase[]}
                  />
                </div>
              </Spacing>
            </Col>
          </Row>
        )}
      </Container>
      {relatedMemories.length > 0 && <HorizontalLine expand={false} />}
      {relatedMemories.length > 0 && (
        <div data-testid="related-memories">
          <Container>
            <Row>
              <Col size={16}>
                <Spacing top={[8, 8, 10]} bottom={[6, 6, -3]}>
                  <H styling={'h2'}>
                    <strong>{t('Lomamuistot')}</strong>
                  </H>
                  <H styling="h5" tag="div" color="gray" data>
                    {t('Kohteessa')} {destination.fields.title}
                  </H>
                </Spacing>
              </Col>
            </Row>
            <DisplayOn small>
              <Row>
                <Col size={24}>
                  <HorizontalCardList visibleCards={VisibleCards.One} expand>
                    {relatedMemories.map(renderMemoryCardSafely)}
                  </HorizontalCardList>
                </Col>
              </Row>
            </DisplayOn>
            <DisplayOn medium>
              <Row>
                <Col size={24}>
                  <HorizontalCardList visibleCards={VisibleCards.Two} expand>
                    {relatedMemories.map(renderMemoryCardSafely)}
                  </HorizontalCardList>
                </Col>
              </Row>
            </DisplayOn>
            <DisplayOn large>
              <DesktopHorizontalList
                elementsPerSlide={3}
                controls={getDesktopHorizontalListTopControls()}
              >
                {relatedMemories.map((memory) => {
                  return (
                    <Spacing key={memory.sys.id} bottom={4}>
                      {renderMemoryCardSafely(memory)}
                    </Spacing>
                  );
                })}
              </DesktopHorizontalList>
            </DisplayOn>
          </Container>
        </div>
      )}
      <Spacing bottom={14}>
        <StickyBottomBox>
          {tripReservationData && (
            <TravelInfoBox
              visible={tripDetailsBoxVisibility}
              imtTripPhaseCities={imtTripPhaseCities}
              availabilityDate={tripReservationData.date}
              shipTrip={shipTrip}
            />
          )}
          <PriceBox
            tripReservationData={tripReservationData}
            tripReservationDataLoadingState={tripReservationDataLoadingState}
            infoButtonOnClick={onPriceBoxInfoButtonClick}
            analyticsCategory={ANALYTICS_CATEGORY}
            analyticsLabel={destination.fields.title}
            contentfulEntry={destination}
          />
        </StickyBottomBox>
      </Spacing>
      <BasicModal
        title={t('Lomakokemus')}
        showDialog={!!experiencePhaseToShow}
        closeDialog={closeExperienceDialog}
        variant="centered"
      >
        {experiencePhaseModalContent}
      </BasicModal>
      {destination.fields.mainImage && (
        <MediaGalleryModal
          items={[
            {
              media: destination.fields.mainImage
            }
          ]}
          closeDialog={() =>
            setMediaGalleryState({ ...mediaGalleryState, show: false })
          }
          showDialog={mediaGalleryState.show}
        />
      )}
      {/*language=CSS*/}
      <style jsx>{`
        @media ${breakpoints.medium} {
          .ingress {
            max-width: ${baseXUnit(95)};
          }
        }
      `}</style>
    </div>
  );
};

DestinationPage.getLayout = (page) =>
  navbarLayout(
    <BackButton
      defaultTitle={t('Matkakohteet')}
      defaultPath={Paths.DestinationList}
    />,
    page,
    [priceBoxHeight, priceBoxHeight, 0] // Add extra space to footer in small & medium.
  );

DestinationPage.getInitialProps = async function ({ query }: NextPageContext) {
  const destinationSlug = String(query.slug);
  let facts = undefined;
  let imtTripId = undefined;
  let imtTripPhaseCities: IMTTripPhaseCities[] = [];
  let relatedMemories: CmsMemory[] = [];
  const destination = await fetchDestination(destinationSlug);
  if (destination) {
    const trip = arrayHead(destination.fields.trips);
    if (trip) {
      imtTripId = trip.fields.erpTripId;
      [facts, imtTripPhaseCities, relatedMemories] = await Promise.all([
        fetchCityFacts(destination.fields.cityErpId),
        fetchTripPhaseCities(imtTripId),
        fetchMemoriesByLinkedEntry(destination.sys.id)
      ]);
    }
  }

  const httpErrorCode = !destination ? 404 : undefined;
  return {
    destination,
    facts,
    relatedMemories,
    imtTripId,
    imtTripPhaseCities,
    httpErrorCode
  };
};

export default DestinationPage;
